/* eslint-disable max-lines-per-function */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Sticky from 'react-stickynode';

import couponsBetaFeatures from 'apps/coupons/betaFeatures';
import {
  generateGraphicBannerElement,
  generateOffersMiniBanners,
  generateCatalogueFreeHtmls,
} from 'apps/scribe/scribeContentProvider';

import CataloguePageMetadata from './meta/CataloguePageMetadata';
import CriteoPageAnalytics from '[theme-lib]/apps/analytics/containers/CriteoPageAnalytics';
import DesktopCatalogueBreadcrumbs from 'apps/catalogue/components/breadcrumbs/DesktopCatalogueBreadcrumbs';
import DisplayOptions from './DisplayOptions';
import { DynamicYieldPageContext, getContextDataForCatalogue } from 'apps/dynamicYield';
import EnforceProductsPageLocation from 'apps/meta/components/EnforceProductsPageLocation';
import ErrorBoundary from 'utils/components/ErrorBoundary';
import ConditionalWrapper from 'utils/ConditionalWrapper';
import ExternalProductsContainer from '[theme-lib]/apps/externalProducts/containers/ExternalProductsContainer';
import H1Tag from 'apps/catalogue/containers/H1Tag';
import ImpressionsPageType from 'apps/gtm/containers/ImpressionsPageType';
import MainFopContainer from 'apps/catalogue/containers/MainFopContainer';
import MobileCatalogueBreadcrumbs from './breadcrumbs/MobileCatalogueBreadcrumbs';
import PageStatistic from 'apps/analytics/components/PageStatistic';
import PageViewType from 'apps/analytics/components/PageViewType';
import PageViewGTMData from 'apps/analytics/components/PageViewGTMData';
import ProductsPageFilterSidebar from './sidebar/ProductsPageFilterSidebar';
import RecommendedFopContainer from '../containers/RecommendedFopContainer';
import SubNavigation from 'apps/navigation/containers/SubNavigation';
import TotalProductNumber from './TotalProductNumber';
import HideOOSCheckLink from './HideOOSCheckLink';
import generateFopCollectionWithCouponsPromoFop from 'apps/coupons/utils/generateFopCollectionWithCouponsPromoFop';
import { getFopBannersFromScribeContent } from '../utils/getFopBanners';

import { isBreadcrumbsWithNewUI } from 'lib/features';

import ExtSectionTypes from 'apps/externalProducts/ExtSectionTypes';
import gtmPageTypes from 'apps/gtm/utils/gtmPageTypes';

import { getCatalogueLink } from '../links';
import { getLastCategoryName } from '../utils/breadcrumbsUtils';
import { pageTypes } from 'apps/analytics';
import PageViewEvent from 'apps/analytics/containers/PageViewEvent';
import { msg } from 'lib/api';

import {
  VIEW_TYPE_PREFERENCE_LIST,
  VIEW_TYPE_PREFERENCE_SHELF,
} from '../actions/catalogue';
import HiddenH1 from '../containers/HiddenH1';

function shouldShowCouponsPromoFop({ betaFeatures, pageParameters, couponsSummary }) {
  return !!couponsSummary &&
    betaFeatures.includes(couponsBetaFeatures.COUPONS20_FOP_ON_OFFERS_PAGE)
    && !(pageParameters.category && pageParameters.category.length)
    && !(pageParameters.filters && pageParameters.filters.length)
    && !(pageParameters.priceFilters && pageParameters.priceFilters.length)
    && !(pageParameters.customFilters && pageParameters.customFilters.length)
    && !pageParameters.hiddenOOS;
}

export default class OnOfferPage extends Component {
  static propTypes = {
    basePath: PropTypes.string.isRequired,
    betaFeatures: PropTypes.arrayOf(PropTypes.string).isRequired,
    fopViewPreference: PropTypes.oneOf([VIEW_TYPE_PREFERENCE_LIST, VIEW_TYPE_PREFERENCE_SHELF]),
    isStickySidebarEnabled: PropTypes.bool,
    openMobileRefineBy: PropTypes.func.isRequired,
    productsPage: PropTypes.shape({
      pageParameters: PropTypes.object.isRequired,
      generalPageInfo: PropTypes.object,
      outOfStockStatistics: PropTypes.array,
      mainFopCollection: PropTypes.object.isRequired,
      navWithFilters: PropTypes.shape({
        filters: PropTypes.array,
      }).isRequired,
      recommendedFopCollection: PropTypes.object,
      sidebarFopCollection: PropTypes.object,
      scribeContent: PropTypes.shape({
        landingPage: PropTypes.shape({
          freeHtmlFiles: PropTypes.array,
          graphicBanner: PropTypes.object,
          promotedBoxes: PropTypes.array,
        }),
        offersLandingPage: PropTypes.shape({
          miniBanners: PropTypes.array,
        }),
      }),
      couponsSummary: PropTypes.object,
      beacons: PropTypes.objectOf(PropTypes.string),
    }).isRequired,
    routeString: PropTypes.string.isRequired,
    isOOSCheckLinkVisible: PropTypes.bool,
  };

  // TODO temporary fix for search redirects issue
  shouldComponentUpdate(nextProps) {
    return !!nextProps.productsPage;
  }

  handleRefineByClick = () => {
    const { openMobileRefineBy, routeString } = this.props;

    openMobileRefineBy(routeString);
  };

  generateSidebarRecommendedFopList() {
    const { betaFeatures, productsPage = {} } = this.props;
    const { recommendedFopCollection = {} } = productsPage;

    if (betaFeatures.includes('EXTERNAL_SIDEBAR_FOP_RECOMMENDATIONS')) {
      return (
        <ExternalProductsContainer
          className="sidebar-element"
          name={ExtSectionTypes.OFFERS_SIDEBAR_RECOMMENDED}
          desktopOnly
        />
      );
    }

    return (
      get(recommendedFopCollection, 'sections')
        ? (
          <RecommendedFopContainer
            fopCollection={recommendedFopCollection}
          />
        )
        : null
    );
  }

  get sidebarCriteoFopList() {
    const { productsPage: { sidebarFopCollection } = {} } = this.props;

    return (
      get(sidebarFopCollection, 'sections.length')
        ? (
          <RecommendedFopContainer
            fopCollection={sidebarFopCollection}
            maxDisplayFops={Number.MAX_SAFE_INTEGER}
            title="Top Brand Offers"
          />
        )
        : null
    );
  }

  generateMainFopList() {
    const {
      betaFeatures,
      productsPage = {},
      routeString,
    } = this.props;
    const { generalPageInfo, mainFopCollection, outOfStockStatistics, pageParameters, couponsSummary } = productsPage;

    const { fopBanner, customFopSection } = getFopBannersFromScribeContent(productsPage.scribeContent);
    const landingPage = get(productsPage, 'scribeContent.landingPage');

    const fopCollection = shouldShowCouponsPromoFop({ betaFeatures, pageParameters, couponsSummary })
      ? generateFopCollectionWithCouponsPromoFop(mainFopCollection, couponsSummary)
      : mainFopCollection;

    return (
      <MainFopContainer
        betaFeatures={betaFeatures}
        customFopSection={customFopSection}
        fopBanner={fopBanner}
        fopCollection={fopCollection}
        generalPageInfo={generalPageInfo}
        gtmPageType="Offers"
        landingPage={landingPage}
        outOfStockStatistics={outOfStockStatistics}
        pageParameters={pageParameters}
        routeString={routeString}
      />
    );
  }

  renderSidebar = () => {
    const linksRoot = '/on-offer';
    const {
      basePath,
      isStickySidebarEnabled,
      productsPage,
      routeString,
    } = this.props;
    const { pageParameters, navWithFilters } = productsPage || {};

    return (
      <div className="sidebar">
        <ConditionalWrapper
          condition={isStickySidebarEnabled}
          wrapper={children => (
            <Sticky
              top=".hd-header"
              bottomBoundary="#main-content"
              activeClass="gn-sticky-sidebar--active"
              enableTransforms={false}
              innerZ={21}
            >
              {children}
            </Sticky>
          )}
        >
          <SubNavigation
            linksRoot={linksRoot}
            productsPage={productsPage}
            forceHidePromo
          />
          <ProductsPageFilterSidebar
            basePath={basePath}
            filters={navWithFilters.filters}
            pageParameters={pageParameters}
            routeString={routeString}
          />
          <ExternalProductsContainer
            className="sidebar-element"
            desktopOnly
            name={ExtSectionTypes.OFFERS_SIDEBAR}
          />
          {this.sidebarCriteoFopList}
          {this.generateSidebarRecommendedFopList()}
        </ConditionalWrapper>
      </div>
    );
  };

  render() {
    const {
      basePath,
      betaFeatures,
      fopViewPreference,
      productsPage,
      routeString,
    } = this.props;
    const { pageParameters, generalPageInfo, scribeContent, beacons = {} } = productsPage || {};

    // TODO temporary fix for search redirects issue
    if (!productsPage) {
      return null;
    }

    const { itemsCount = 0 } = generalPageInfo;
    const isSelectedItemPage = get(generalPageInfo, 'displayOptions.showSelectedItemsOnly', false);
    const subcategories = get(productsPage, 'navWithFilters.subcategories');
    const scribeMetaData = {
      categoryLandingPageTitle: get(scribeContent, 'landingPage.pageTitle'),
      categoryPageMetaDescription: get(scribeContent, 'landingPage.metaDescription'),
      eventLandingPageTitle: get(scribeContent, 'eventLandingPage.pageTitle'),
      eventPageMetaDescription: get(scribeContent, 'eventLandingPage.metaDescription'),
    };
    const categories = [{ name: 'Offers' }, ...(pageParameters.category || [])];
    const pageCategory = categories.length > 0 ? categories[categories.length - 1].name : undefined;
    const aisleId = categories[categories.length - 1].id;
    const pageBreadcrumb = categories.map(
      item => item.name
    ).join(
      ' / '
    ) || undefined;
    const filters = (pageParameters.filters || []).map((filter) => `${filter.filterType}:${filter.name}`).join('|');
    const { sort } = pageParameters;
    const pageViewGTMData = {
      pageType: isSelectedItemPage ? pageTypes.OFFERS_LANDING : pageTypes.OFFERS_PRODUCTS,
      pageCategory,
      pageBreadcrumb,
      filters,
      sort,
      aisleId,
    };

    return (
      <div>
        <PageViewEvent eventData={pageViewGTMData} />
        <PageViewType pageType={isSelectedItemPage ? pageTypes.OFFERS_LANDING : pageTypes.OFFERS_PRODUCTS} />
        <PageViewGTMData data={pageViewGTMData} />
        <ImpressionsPageType pageType={gtmPageTypes.Offers} />
        <EnforceProductsPageLocation
          href={getCatalogueLink(pageParameters, '/on-offer')}
          pageParameters={pageParameters}
          routeString={routeString}
        />
        <CataloguePageMetadata
          basePath={basePath}
          scribeMetaData={scribeMetaData}
          pageParameters={pageParameters}
          subcategories={subcategories}
        />
        <DynamicYieldPageContext {...getContextDataForCatalogue(pageParameters.category)} />
        <ErrorBoundary>
          {this.renderSidebar()}
          <div className="main-column">
            <DesktopCatalogueBreadcrumbs
              baseName="Offers"
              baseNameNewUI="Offers"
              basePath="/on-offer"
              pageParameters={pageParameters}
              withNewUI={isBreadcrumbsWithNewUI(betaFeatures)}
            />
            {generateGraphicBannerElement(
              get(scribeContent, 'landingPage.graphicBanner'), get(scribeContent, 'landingPage')
            )}
            {scribeContent ? generateOffersMiniBanners(scribeContent) : null}
            {generateCatalogueFreeHtmls(scribeContent)}
            <ExternalProductsContainer name={ExtSectionTypes.OFFERS_BANNER} />
            <H1Tag lastCategoryName={getLastCategoryName(pageParameters)} />
            <HiddenH1>
              {
                isSelectedItemPage
                  ? msg.fop.hiddenH1
                  : (`${getLastCategoryName(pageParameters)} On Offer`)
              }
            </HiddenH1>
            <div className="catHeaderBar">
              <MobileCatalogueBreadcrumbs
                baseName="Offers"
                categories={pageParameters.category}
                cluster={pageParameters.cluster}
                filters={pageParameters.filters}
                itemsCount={itemsCount}
              />
              <div className="desktop-product-count show-for-xlarge">
                <TotalProductNumber itemsCount={itemsCount} />
              </div>
              {this.props.isOOSCheckLinkVisible && (
                <HideOOSCheckLink
                  checked={pageParameters.hiddenOOS}
                  link={
                    getCatalogueLink({
                      ...pageParameters,
                      hiddenOOS: !pageParameters.hiddenOOS,
                    }, basePath)
                  }
                />
              )}
              <DisplayOptions
                pageParameters={pageParameters}
                betaFeatures={betaFeatures}
                displaySort={!isSelectedItemPage}
                onRefineByClick={this.handleRefineByClick}
              />
              <PageStatistic field="FOP_VIEW_PREFERENCE" value={fopViewPreference} />
            </div>
            {this.generateMainFopList()}
          </div>
          <CriteoPageAnalytics betaFeatures={betaFeatures} beacons={beacons} />
        </ErrorBoundary>
      </div>
    );
  }
}
